import { useCallback, useEffect, useMemo, useState } from "react";
import { getInquiriesByOrganizationAsync } from "../api/InquiryApi";
import {
  ContactInquiry,
  ContactInquiryStatus,
} from "../models/InquiryModels";
import { Constants } from "../constants/constants";

interface UseInquiriesReturn {
  inquiries: ContactInquiry[];
  loading: boolean;
  error: string | null;
  status: ContactInquiryStatus | undefined;
  setStatus: (status: ContactInquiryStatus | undefined) => void;
  reload: () => Promise<void>;
}

export const useInquiries = (
  organizationId: string | undefined,
): UseInquiriesReturn => {
  const [inquiries, setInquiries] = useState<ContactInquiry[]>([]);
  const [status, setStatus] = useState<ContactInquiryStatus | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  const load = useCallback(async () => {
    if (!organizationId || !token) {
      setInquiries([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getInquiriesByOrganizationAsync(
        token,
        organizationId,
        status,
      );
      if (res.success && res.result) {
        setInquiries(res.result);
        setError(null);
      } else {
        setError(res.message || "Failed to load inquiries");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  }, [organizationId, token, status]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const sortedInquiries = useMemo(
    () =>
      [...inquiries].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [inquiries],
  );

  return {
    inquiries: sortedInquiries,
    loading,
    error,
    status,
    setStatus,
    reload,
  };
};
